import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';

import { selectRTFO } from '../../reducers/rtfoSlice';
import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { useTheme } from '@mui/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { VerticalAlignBottom } from '@mui/icons-material';

import { useSnackbar } from 'notistack';
import { useSocket } from '../../context/useSocket';
import { rtfoConversionFactors } from './rtfoConversionFactors';

const RTFOISIPEdit = ({ setIsEditMode }) => {
  const theme = useTheme();
  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;

  const { enqueueSnackbar } = useSnackbar();

  const { socket } = useSocket();

  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const units = currentRTFO?.settings?.units || 'metric';

  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];
  const stageData = currentStage?.coldboreData;

  const [editMode, setEditMode] = useState(false);
  const [tabItem, setTabItem] = useState(0);
  const [isip, setISIP] = useState((currentStage?.metrics?.isip * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(2) || 0);
  const [isipTimestamps, setISIPTimestamps] = useState(
    currentStage?.isipData?.isipTimestamp?.startTimestamp && currentStage?.isipData?.isipTimestamp?.endTimestamp
      ? [currentStage?.isipData?.isipTimestamp?.startTimestamp, currentStage?.isipData?.isipTimestamp?.endTimestamp]
      : null
  );

  const currentISIPStart = useRef(
    [currentStage?.isipData?.isipTimestamp?.startTimestamp, currentStage?.isipData?.isipTimestamp?.endTimestamp] || null
  );

  const xRangeRef = useRef(null);

  // Function to get current range after user interaction or initial set
  const getCurrentRange = (plotlyEvent) => {
    if (plotlyEvent && plotlyEvent['xaxis.range[0]'] && plotlyEvent['xaxis.range[1]']) {
      xRangeRef.current = [plotlyEvent['xaxis.range[0]'], plotlyEvent['xaxis.range[1]']];
    };
  };

  useEffect(() => {
    setISIP((currentStage?.metrics?.isip * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(2) || 0);
    // setIsEditMode(false);
    setISIPTimestamps(
      currentStage?.isipData?.isipTimestamp?.startTimestamp && currentStage?.isipData?.isipTimestamp?.endTimestamp
        ? [currentStage?.isipData?.isipTimestamp?.startTimestamp || 0, currentStage?.isipData?.isipTimestamp?.endTimestamp || 0]
        : null
    );
    currentISIPStart.current = currentStage?.isipData?.isipTimestamp
      ? [currentStage?.isipData?.isipTimestamp?.startTimestamp, currentStage?.isipData?.isipTimestamp?.endTimestamp]
      : null;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRTFO?.currentStageName]);

  const data = useMemo(() => {
    if (!stageData || !stageData?.length > 0) return [];

    return getChartData();

    function getChartData() {
      try {
        const X = [];
        const YPressure = [];
        const YRate = [];
        const YProducedRate = [];
        const YPropConcSurf = [];
        const YPropConcBH = [];
        const YFR = [];
        const YFR2 = [];

        stageData?.forEach((d, i) => {
          if (d.timestamp > 0) {
            X.push(new Date(d.timestamp));
            YPressure.push((d.pressure || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
            YRate.push((d.rate || 0) * (units === 'field' ? rtfoConversionFactors.rate : 1));
            YProducedRate.push((currentRTFO?.name === '03-21B' ? d.clean * 0.1 : d.producedRate || 0) * (units === 'field' ? rtfoConversionFactors.clean : 1)); // TEMPORARILY ADDED FOR 03-21B PAD
            YPropConcSurf.push((d.proppant || 0) * (units === 'field' ? rtfoConversionFactors.proppant : 1));
            YPropConcBH.push((d.proppantBH || 0) * (units === 'field' ? rtfoConversionFactors.proppantBH : 1));
            YFR.push((d.fr || 0) * (units === 'field' ? rtfoConversionFactors.fr * 10 : 1));
            YFR2.push((d.fr2 || 0) * (units === 'field' ? rtfoConversionFactors.fr2 : 1));
          }
        });

        // add traces
        let treatingPressureTrace = {
          x: X,
          y: YPressure,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: `Treating Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
          line: {
            width: 2,
            color: chartColors[2]
          },
          connectgaps: false
        };
        let rateTrace = {
          x: X,
          y: YRate,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y2',
          xaxis: 'x1',
          name: `Slurry Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
          line: {
            width: 2,
            color: chartColors[3]
          },
          connectgaps: false
        };
        let propConcSurfTrace = {
          x: X,
          y: YPropConcSurf,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y3',
          xaxis: 'x1',
          name: `Propp. Surf. (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
          line: {
            width: 2,
            color: chartColors[4]
          }
        };
        let propConcBHTrace = {
          x: X,
          y: YPropConcBH,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y3',
          xaxis: 'x1',
          name: `Propp. BH (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
          line: {
            width: 2,
            color: chartColors[6]
          }
        };
        let frConcTrace = {
          x: X,
          y: YFR,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y4',
          xaxis: 'x1',
          name: `DFR Conc. (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
          line: {
            width: 2,
            color: chartColors[8],
            dash: 'dot'
          }
        };
        let fr2ConcTrace = {
          x: X,
          y: YFR2,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y4',
          xaxis: 'x1',
          name: `LFR Conc. (${units === 'field' ? 'gal/mgal' : 'L/m3'})`,
          line: {
            width: 2,
            color: chartColors[8],
            dash: 'dash'
          }
        };

        let traces = [treatingPressureTrace, rateTrace, propConcSurfTrace, propConcBHTrace, frConcTrace, fr2ConcTrace];

        return traces;
      } catch (error) {
        console.log(error);
        return [];
      }
    }
  }, [stageData, units, currentRTFO?.name]);


  const layout = useMemo(() => {
    return {
      yaxis: {
        // Treating pressure y axis
        linecolor: chartColors[2],
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: chartColors[2],
        title: {
          text: `Treating Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
          font: {
            size: 12
          }
        },
        anchor: 'x',
        range: units === 'field' ? [0, 15000] : [0, 100],
        side: 'left'
      },
      yaxis2: {
        // Slurry rate y axis
        linecolor: chartColors[3],
        showgrid: false,
        color: chartColors[3],
        title: {
          text: `Slurry Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
          font: {
            size: 12
          }
        },
        range: units === 'field' ? [0, 150] : [0, 25],
        anchor: 'free',
        overlaying: 'y',
        side: 'left',
        position: 0
      },
      yaxis3: {
        // Prop conc surf y axis
        linecolor: chartColors[4],
        showgrid: false,
        color: chartColors[4],
        title: {
          text: `Propp. Conc. (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
          font: {
            size: 12
          }
        },
        range: units === 'field' ? [0, 10] : [0, 1000],
        overlaying: 'y',
        side: 'right',
        anchor: 'x'
      },
      yaxis4: {
        // FR conc y axis
        linecolor: chartColors[8],
        showgrid: false,
        color: chartColors[8],
        title: {
          text: `FR Conc. (${units === 'field' ? 'lb/gal or gal/mgal' : 'kg/m3 or L/m3'})`,
          font: {
            size: 12
          }
        },
        range: [0, 100],
        overlaying: 'y',
        side: 'right',
        anchor: 'free',
        position: 1,
        uirevision: 'true'
      },
      xaxis: {
        // Date x axis
        range: xRangeRef.current || (currentISIPStart.current
          ? [new Date(currentISIPStart.current?.[0] - 10 * 60 * 1000), new Date(currentISIPStart.current?.[1] + 10 * 60 * 1000)]
          : [new Date(currentStage?.coldboreData?.[0]?.timestamp), new Date(currentStage?.coldboreData?.slice(-1)?.[0]?.timestamp)]),
        title: {
          text: 'Date',
          font: {
            size: 12
          }
        },
        domain: [0.1, 0.9],
        linecolor: darkMode ? 'lightgrey' : 'black',
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: darkMode ? 'lightgrey' : 'black',
        anchor: 'y',
        showspikes: true,
        spikethickness: 2,
        spikedash: 'dot',
        spikecolor: '#999999',
        spikemode: 'across',
        type: 'date'
      },
      hovermode: 'x',
      hoverdistance: 20, // Distance to show hover label of data point
      spikedistance: 1000,
      autosize: true,
      showlegend: true,
      legend: {
        orientation: 'h',
        yanchor: 'top',
        font: {
          color: darkMode ? 'lightgrey' : 'grey',
          size: 10
        },
        y: -0.2
      },
      annotations: [
        {
          text: 'Pumping Schedule',
          font: {
            size: 14,
            color: darkMode ? 'lightgrey' : 'black'
          },
          showarrow: false,
          align: 'center',
          x: 0.5, //position in x domain
          y: 1.15, //position in y domain
          xref: 'paper',
          yref: 'paper'
        },
        {
          text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber || 'No stage selected'}`,
          font: {
            size: 10,
            color: darkMode ? 'lightgrey' : 'black'
          },
          showarrow: false,
          align: 'center',
          x: 0.5, //position in x domain
          y: 1.075, //position in y domain
          xref: 'paper',
          yref: 'paper'
        },
        currentStage?.events?.preSand?.[0]?.timestamp && {
          // pre sand line annotation
          x: new Date(currentStage?.events?.preSand?.[0]?.timestamp),
          y: 1,
          xref: 'x',
          yref: 'paper',
          text: 'Pre Sand',
          textangle: '-90',
          bgcolor: darkMode ? '#37474f' : '#fff',
          font: { color: darkMode ? '#f5f5f5' : '#333' },
          borderpad: 1,
          showarrow: false,
          // arrowhead: 7,
          ax: 0,
          ay: -40
        }
      ],
      shapes: [
        currentStage?.events?.preSand?.[0]?.timestamp && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(currentStage?.events?.preSand?.[0]?.timestamp),
          y0: 0,
          x1: new Date(currentStage?.events?.preSand?.[0]?.timestamp),
          y1: 0.99,
          line: {
            color: darkMode ? '#f5f5f5' : '#333',
            width: 2,
            dash: 'dot'
          }
        },
        isipTimestamps?.[0] &&
          isipTimestamps?.[1] && {
            type: 'rect',
            xref: 'x',
            yref: 'paper',
            x0: new Date(isipTimestamps?.[0]),
            y0: 0,
            x1: new Date(isipTimestamps?.[1]),
            y1: 1,
            line: {
              width: 0
            },
            fillcolor: 'green',
            opacity: 0.1
          },
        isipTimestamps?.[0] && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(isipTimestamps?.[0]),
          y0: 0,
          x1: new Date(isipTimestamps?.[0]),
          y1: 1,
          line: {
            color: 'black',
            width: 2,
            dash: 'dot'
          },
          opacity: 0.5
        },
        isipTimestamps?.[1] && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(isipTimestamps?.[1]),
          y0: 0,
          x1: new Date(isipTimestamps?.[1]),
          y1: 1,
          line: {
            color: 'black',
            width: 2,
            dash: 'dot'
          },
          opacity: 0.5
        },
        isipTimestamps?.[0] &&
          editMode === 'start' && {
            type: 'line',
            xref: 'x',
            yref: 'paper',
            x0: new Date(isipTimestamps?.[0]),
            y0: 0,
            x1: new Date(isipTimestamps?.[0]),
            y1: 1,
            line: {
              color: 'green',
              width: 2
            },
            opacity: 0.5
          },
        isipTimestamps?.[1] &&
          editMode === 'end' && {
            type: 'line',
            xref: 'x',
            yref: 'paper',
            x0: new Date(isipTimestamps?.[1]),
            y0: 0,
            x1: new Date(isipTimestamps?.[1]),
            y1: 1,
            line: {
              color: 'green',
              width: 2
            },
            opacity: 0.5
          }
      ],
      uirevision: 'true',
      margin: { t: 60, l: 60, r: 60, b: 60 },
      images: [
        {
          x: 0.035,
          y: 1.025,
          sizex: 0.1,
          sizey: 0.1,
          source: `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
          xref: 'page',
          yref: 'y1 domain',
          xanchor: 'right',
          yanchor: 'bottom',
          opacity: 0.75
        }
      ],
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent'
      // paper_bgcolor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light']
    };
  }, [currentRTFO?.name, currentStage?.coldboreData, currentStage?.events?.preSand, currentStage?.stageNumber, currentStage?.wellName, darkMode, editMode, isipTimestamps, units]);

  const onRelayout = (event) => {
    getCurrentRange(event);
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: 'RTFO-' + new Date().toISOString().split('T')[0],
      // filename: currentFBA?.name + "-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <Dialog 
      open={true} 
      fullWidth={true} 
      maxWidth="md" 
      style={{ height: '100%' }} 
      onClose={() => setIsEditMode(false)}
      PaperProps={{
        style: {
          backgroundColor: theme.palette.paper[darkMode ? 'dark' : 'light'],
          boxShadow: 'none'
        }
      }}
      elevation={0}
    >
      <DialogTitle>Edit ISIP</DialogTitle>
      <DialogContent>
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            overflow: 'hidden'
          }}
        >
          <Tabs
            value={tabItem}
            textColor={darkMode ? 'inherit' : 'primary'}
            TabIndicatorProps={{ style: { background: darkMode ? '#fff' : theme?.palette?.primary?.main } }}
            onChange={(e, value) => setTabItem(value)}
            style={{
              height: 24
            }}
          >
            <Tab label="Automatic" />
            <Tab label="Manual" />
          </Tabs>

          {tabItem === 0 ? (
            <div>
              <ToggleButtonGroup
                value={editMode}
                exclusive
                onChange={(e, newType) => setEditMode(newType)}
                size="small"
                style={{ height: 24, zIndex: 420, marginTop: 10 }}
                disabled={currentStage?.fracModelLocked}
              >
                <ToggleButton value="start" size="small" title="Edit ISIP Start" color={darkMode ? '' : 'primary'}>
                  <VerticalAlignBottom
                    style={{
                      transform: 'rotate(90deg)',
                      fontSize: '16px'
                    }}
                  />
                </ToggleButton>
                <ToggleButton value="end" size="small" title="Edit ISIP End" color={darkMode ? '' : 'primary'}>
                  <VerticalAlignBottom
                    style={{
                      transform: 'rotate(-90deg)',
                      fontSize: '16px'
                    }}
                  />
                </ToggleButton>
              </ToggleButtonGroup>

              {/* {editMode && (
                <ToggleButtonGroup
                  value={''}
                  size="small"
                  style={{
                    height: 24,
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    zIndex: 420
                  }}
                  onChange={() => {}}
                >
                  <ToggleButton
                    value={'1'}
                    size="small"
                    title="Cancel Changes"
                    color={darkMode ? '' : 'primary'}
                    onClick={handleCancelChanges}
                  >
                    <Cancel style={{ fontSize: '16px' }} />
                  </ToggleButton>
                  <ToggleButton value={'2'} size="small" title="Save Changes" color={darkMode ? '' : 'primary'} onClick={handleSaveChanges}>
                    <Save style={{ fontSize: '16px' }} />
                  </ToggleButton>
                </ToggleButtonGroup>
              )} */}
              <Plot
                data={data}
                layout={layout}
                onRelayout={onRelayout}
                config={config}
                useResizeHandler={true}
                style={{ width: '100%', height: '100%' }}
                onClick={e => handlePlotClick(e)}
              />
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'relative',
                width: '100%',
                marginTop: 20,
                marginBottom: 10
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
                <Typography variant="caption">Surface ISIP:</Typography>
                <TextField
                  label="Surface ISIP"
                  value={isip}
                  onChange={handleISIPChange}
                  size="small"
                  style={{ marginRight: 10, marginLeft: 10 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{units === 'field' ? 'psi' : 'MPa'}</InputAdornment>
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button size="small" color={darkMode ? "inherit" : "secondary"} onClick={handleCancelChanges}>
          Cancel
        </Button>
        <Button size="small" color={darkMode ? "inherit" : "secondary"} variant="contained" onClick={handleSaveChanges} disabled={!isipTimestamps && !isip}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );

  // FUNCTIONS
  function handlePlotClick(e) {
    try {
      const clickedTimestamp = new Date(e.points?.[0]?.x)?.getTime();

      if (!editMode) {
        return;
      } else if (editMode === 'start') {
        setISIPTimestamps(prev => [clickedTimestamp, prev?.[1]]);
      } else if (editMode === 'end') {
        setISIPTimestamps(prev => [prev?.[0], clickedTimestamp]);
      };

      return;
    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleCancelChanges() {
    setISIP(currentStage?.metrics?.isip || 0);
    setEditMode(false);
    setIsEditMode(false);
  };

  function handleSaveChanges() {
    if (!socket?.readyState === 1) return;

    const newISIP = +isip / (units === 'field' ? rtfoConversionFactors.pressure : 1);;
    let newISIPParams = null;
    let newISIPTimestamp =
      isipTimestamps?.[0] && isipTimestamps?.[1] ? { startTimestamp: isipTimestamps?.[0], endTimestamp: isipTimestamps?.[1] } : null;

    if (tabItem === 1 && newISIP > 0) {
      const stageName = currentRTFO?.currentStageName;
      const wellName = stageName?.split('_')?.[1];
      const stageNumber = String(
        Number(
          String(stageName?.split('_')?.[2])
            ?.replace(/[^0-9.]+/g, '')
            ?.trim()
        )
      );
      const stageClusters = currentRTFO?.perfSchedule?.wells?.[wellName?.toLowerCase()]?.stages?.[stageNumber]?.clusters;
      const depths = stageClusters ? Object.keys(stageClusters)?.map(cluster => stageClusters[cluster]?.shotDepth) : [];
      const mdAvg = depths?.reduce((tot, curr) => tot + curr, 0) / depths?.length || 0;
      const tvd =
        currentRTFO?.surveys?.wells?.[wellName?.toLowerCase()]?.surveyData?.find(data => data.measuredDepth >= mdAvg)?.trueVerticalDepth ||
        0;

      const isipBH = ((newISIP * 1000 + tvd * 9.8) / 1000);
      const fracGradient = ((isipBH * 1000) / tvd || 0);

      newISIPParams = {
        intercept: newISIP,
        isipBH,
        fracGradient
      };
    };

    socket.send(JSON.stringify({
      messageType: 'recalculateStageISIP',
      padName: currentRTFO?.name,
      stageName: currentStage?.name,
      isipTimestamp: tabItem === 0 ? newISIPTimestamp : null,
      isipParams: tabItem === 1 ? newISIPParams : null
    }));

    enqueueSnackbar(`Processing stage ${currentStage?.name}...`, { variant: 'info', persist: true });
    setIsEditMode(false);
  };

  function handleISIPChange(e) {
    const value = e.target.value;
    setISIP(value);
  };
};

export default RTFOISIPEdit;
